import React from 'react'
import Layout from '../components/layout/Layout'
import Seo from '../components/Seo'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

import { showConsentSettings } from '../utils/cookieConsentConfig'

const PrivacyPolicy: React.FC = () => {
  return (
    <Layout>
      <Seo title="Privacy Policy — 2homes.app" canonical="https://2homes.app/privacy-policy" robots="index,follow" />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-4">
          <Link to="/" className="inline-flex items-center text-slate-700 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to home
          </Link>
        </div>
        <div className="card-glass p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
            Privacy Policy 2homes.app
          </h1>
          <p className="text-sm text-slate-500 mb-8">Last updated: 04 Jan 2026</p>

          <div className="prose prose-slate max-w-none">
            <h2><strong>1) Data Controller</strong></h2>
            <p>
              The controller of your personal data is AITRAIN Marcin Opinc, ul. Kaszubska 6/21, 83-110 Tczew, Poland,
              VAT ID (NIP): 5831541445, e-mail: <a href="mailto:contact@2homes.app">contact@2homes.app</a>, tel. (+48) 691 300 548 ("Administrator", "we").
            </p>

            <h2><strong>2) Scope and purpose of the document</strong></h2>
            <p>
              This Policy explains what data we process in connection with your use of the 2homes.app service and web application (PWA) ("Service"), for what purposes, on what legal basis, for how long, to whom we disclose it, and what your rights are.
              The Service does not display advertisements and does not conduct advertising profiling.
            </p>

            <h2><strong>3) Categories of data we process</strong></h2>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>Account Data:</strong> e-mail, password (encrypted), name/nickname, account settings.</li>
              <li><strong>Service Data:</strong> content added in the Service (e.g., calendar events, lists, notes, tasks, files – if the feature is used).
              </li>
              <li><strong>Billing Data:</strong> subscription information, payment status, transaction identifiers. We do not process payment card data – this is handled by the payment operator.</li>
              <li><strong>Consent Data:</strong> records of your consents (Terms of Service, Privacy Policy acceptance, marketing communications consent) with version numbers and timestamps.</li>
              <li><strong>Technical/Usage Data:</strong> IP address, device and session identifiers, server logs, error data.</li>
              <li><strong>Analytical Data (Google Analytics)</strong> – aggregated data on traffic and user behavior.</li>
              <li><strong>Cookies and similar technologies</strong> – details in the Cookie Policy.</li>
            </ul>

            <h2><strong>4) Purposes and legal bases (GDPR Art. 6)</strong></h2>
            <table>
              <thead>
                <tr><th>Purpose</th><th>Legal basis</th></tr>
              </thead>
              <tbody>
                <tr><td>Account creation and maintenance, providing the 2homes.app service</td><td>necessity for the performance of a contract (Art. 6(1)(b))</td></tr>
                <tr><td>Online payments (Stripe), accounting, and handling complaints</td><td>necessity for a contract (b), legal obligation (c)</td></tr>
                <tr><td>Handling inquiries, e-mail communication</td><td>legitimate interest (f)</td></tr>
                <tr><td>Maintaining Service security (logs, protection against abuse)</td><td>legitimate interest (f)</td></tr>
                <tr><td>Analytics (Google Analytics 4, in EU-compliant mode)</td><td>consent (a)</td></tr>
                <tr><td>Marketing communications (newsletters, special offers, promotional emails)</td><td>consent (a)</td></tr>
                <tr><td>Pursuing and defending claims</td><td>legitimate interest (f)</td></tr>
                <tr><td>Accounting/tax settlements</td><td>legal obligation (c)</td></tr>
              </tbody>
            </table>

            <h2><strong>5) Data Recipients</strong></h2>
            <p>Your data may be shared with the following categories of entities:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>Payment Operator:</strong> Stripe Payments Europe, Limited – independent payment data controller.</li>
              <li><strong>Hosting and Infrastructure:</strong> Vercel Inc. – hosting and running the application.</li>
              <li><strong>Database:</strong> Convex Inc. – reactive database and server functions.</li>
              <li><strong>Authentication:</strong> Clerk Inc. – user authentication and identity management.</li>
              <li><strong>Email/SMTP:</strong> Resend Inc. – sending notifications and system messages.</li>
              <li><strong>Analytics:</strong> Google Ireland Limited – Google Analytics 4 (if you consent).</li>
              <li><strong>Accounting and Legal Support</strong> – to the extent necessary for settlements and legal protection.</li>
            </ul>
            <p>We conclude a Data Processing Agreement (DPA) with each processor. For global providers, DPAs are available in their terms or automatically accepted when using the services.</p>

            <h2><strong>6) Transfer of data outside the EEA</strong></h2>
            <p>Some providers (e.g., Google, Vercel, Convex, Clerk, Resend) are based outside the EEA. In such cases, we use mechanisms required by the GDPR, including Standard Contractual Clauses (SCC) and additional technical safeguards (encryption, data minimization).</p>

            <h2><strong>7) Retention Period</strong></h2>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>Account Data and Service Data</strong> – for the duration of the contract, and up to 6 months after its termination.</li>
              <li><strong>Payment Data and Accounting</strong> – up to 5 years from the end of the tax year.</li>
              <li><strong>Technical Logs</strong> – up to 12 months.</li>
              <li><strong>Analytical Data (GA4)</strong> – according to tool settings (14–26 months) or shorter if you withdraw consent.</li>
            </ul>

            <h2><strong>8) Voluntary provision of data</strong></h2>
            <p>Providing data required for registration and payment is necessary to use the service. Analytical data/cookies and marketing communications consent are voluntary. You can use the Service without consenting to marketing communications.</p>

            <h2><strong>9) Your Rights</strong></h2>
            <p>You have the right to:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>access, rectification, erasure, restriction of processing,</li>
              <li>data portability,</li>
              <li>object to processing (when based on legal interest),</li>
              <li>withdraw consent at any time (without affecting compliance before withdrawal).</li>
            </ul>
            <p>You can lodge complaints with the supervisory authority: President of the Personal Data Protection Office (UODO), ul. Stawki 2, 00-193 Warsaw, <a href="https://uodo.gov.pl" target="_blank" rel="noopener noreferrer">uodo.gov.pl</a>.</p>

            <h2><strong>10) Security</strong></h2>
            <p>We use, among others, transmission encryption (TLS/HTTPS), password hashing, access control, and backups.</p>

            <h2><strong>11) Children</strong></h2>
            <p>The Service is directed to adults. If you are under 16, use the Service only with the consent of a guardian.</p>

            <h2><strong>12) Cookies and Consent Management</strong></h2>
            <p>
              We use cookies and similar technologies to ensure the proper functioning of the Service (essential cookies) and, with your consent, for analytical purposes (Google Analytics 4).
            </p>
            <p>
              For compliance purposes, we log your consent status (including a unique identifier, accepted categories, timestamp, and policy version) to a secure record.
            </p>
            <p>
              You can change your cookie preferences at any time by clicking the button below or the "Manage consent" link in the footer.
            </p>
            <div className="my-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  showConsentSettings();
                }}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-md text-sm font-medium text-slate-800 transition-colors"
              >
                Manage Cookie Preferences
              </button>
            </div>

            <h2><strong>13) Marketing Communications Consent</strong></h2>
            <p>
              During account registration, you may optionally consent to receive marketing communications from us (newsletters, special offers, promotional emails, discount coupons, etc.).
            </p>
            <p>
              <strong>How it works:</strong>
            </p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Consent to marketing communications is <strong>voluntary</strong> and not required to use the Service.</li>
              <li>If you consent, we will record your consent in our database with a timestamp and version number.</li>
              <li>You can withdraw your marketing consent at any time by contacting us at <a href="mailto:contact@2homes.app">contact@2homes.app</a> or by using the unsubscribe link in any marketing email.</li>
              <li>Withdrawing marketing consent does not affect your use of the Service or other consents (Terms, Privacy Policy).</li>
              <li>We will stop sending marketing communications within 30 days of receiving your withdrawal request.</li>
            </ul>
            <p>
              <strong>What we send:</strong> We may send you information about new features, special offers, seasonal promotions (e.g., holiday discounts), product updates, and other relevant information about 2homes.app. We do not send spam or share your email with third parties for marketing purposes.
            </p>
            <p>
              <strong>Consent records:</strong> We maintain a record of your consent (including version and timestamp) for compliance and audit purposes, in accordance with GDPR requirements.
            </p>

            <h2><strong>14) Policy Changes</strong></h2>
            <p>The Policy may be updated – the date at the top indicates the latest version. If significant changes are made, we will notify you via email (if you have an account) or through a notice on the Service.</p>

            <h2><strong>15) Contact</strong></h2>
            <p>
              E‑mail: <a href="mailto:contact@2homes.app">contact@2homes.app</a><br />
              Tel.: (+48) 691 300 548<br />
              Address: AITRAIN Marcin Opinc, ul. Kaszubska 6/21, 83-110 Tczew, Poland
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default PrivacyPolicy 
